/**
 * PaperJournalList page — REW-929 Phase 3.
 *
 * Composes filters + summary card + history table + pagination + upload modal.
 * Upload button is gated by `paper_journal:write`. Read access is enforced at
 * the route layer via ProtectedRoute.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/common/Modal';
import { Table, Pagination, Column } from '@/components/common/Table';
import HasPermission from '@/components/common/HasPermission';
import { usePaperJournalData } from '@/app/hooks/usePaperJournalData';
import { PaperJournalFilters } from '@/pages/PaperJournal/PaperJournalFilters';
import { PaperJournalSummaryCard } from '@/pages/PaperJournal/PaperJournalSummaryCard';
import { PaperJournalUploadModal } from '@/pages/PaperJournal/PaperJournalUploadModal';
import type { PaperJournalUploadHistoryItem } from '@/app/types/paperJournal';

import './PaperJournalList.scss';

function _formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const COLUMNS: Column<PaperJournalUploadHistoryItem>[] = [
  {
    key: 'uploaded_at',
    header: 'Uploaded at',
    render: (item) => _formatDate(item.uploaded_at),
  },
  {
    key: 'original_filename',
    header: 'File',
  },
  {
    key: 'uploaded_by',
    header: 'Uploaded by',
  },
  {
    key: 'row_count',
    header: 'Rows',
    align: 'right',
    render: (item) => item.row_count.toLocaleString(),
  },
  {
    key: 'accepted_count',
    header: 'Accepted',
    align: 'right',
    render: (item) => item.accepted_count.toLocaleString(),
  },
  {
    key: 'rejected_count',
    header: 'Rejected',
    align: 'right',
    render: (item) => item.rejected_count.toLocaleString(),
  },
  {
    key: 'drill_down',
    header: 'Detail',
    render: (item) => (
      <Link to={`/paper-journal/uploads/${item.file_sha256}`}>View rows</Link>
    ),
  },
];

interface PaperJournalListProps {
  /** When true, rendered inside PaperJournalPage (no duplicate page title / lookup nav). */
  embedded?: boolean;
}

export const PaperJournalList: React.FC<PaperJournalListProps> = ({ embedded = false }) => {
  const data = usePaperJournalData();
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const openUpload = () => {
    data.dismissUploadResult();
    setIsUploadOpen(true);
  };

  const closeUpload = () => {
    setIsUploadOpen(false);
  };

  return (
    <div className="paper-journal-list">
      <header className="paper-journal-list__header">
        {!embedded && (
          <h1 className="paper-journal-list__title">Paper Journal Uploads</h1>
        )}
        <div
          className={`paper-journal-list__header-actions${embedded ? ' paper-journal-list__header-actions--embedded' : ''}`}
        >
          <HasPermission permission="paper_journal:write">
            <Button onClick={openUpload}>Upload CSV</Button>
          </HasPermission>
        </div>
      </header>

      <PaperJournalFilters
        initial={data.filters}
        onApply={data.applyFilters}
        onClear={data.resetFilters}
        onWindowChange={data.applySummaryWindow}
      />

      <PaperJournalSummaryCard summary={data.summary} />

      {data.error !== null && (
        <div className="paper-journal-list__error" role="alert">
          {data.error}
        </div>
      )}

      <Table<PaperJournalUploadHistoryItem>
        columns={COLUMNS}
        data={data.uploads}
        keyExtractor={(item) => item.file_sha256}
        loading={data.isLoading}
        emptyMessage="No paper journal uploads yet."
      />

      {data.uploads.length > 0 && (
        <Pagination
          currentPage={data.currentPage}
          totalPages={data.totalPages}
          totalItems={data.totalCount}
          itemsPerPage={data.pageSize}
          onPageChange={data.goToPage}
          pageSizeOptions={data.pageSizeOptions}
          onPageSizeChange={data.changePageSize}
        />
      )}

      <PaperJournalUploadModal
        isOpen={isUploadOpen}
        onClose={closeUpload}
        uploadPaperJournal={data.uploadPaperJournal}
        uploading={data.uploading}
        lastUploadResponse={data.lastUploadResponse}
        uploadError={data.uploadError}
        dismissUploadResult={data.dismissUploadResult}
      />
    </div>
  );
};

export default PaperJournalList;
